// src/pages/landing/LandingPage.jsx
import { useNavigate } from "react-router-dom";
import { Briefcase, Lock, Zap, Search, Shield, ArrowRight } from "lucide-react";
import styles from "./LandingPage.module.css";

const MOCK_JOBS = [
  { title: "Senior React Developer",       loc: "Remote · Cape Town",   type: "Contract",  time: "2h ago",  locked: true  },
  { title: "UX/UI Designer",               loc: "Johannesburg, GP",      type: "Freelance", time: "5h ago",  locked: false },
  { title: "Digital Marketing Manager",    loc: "Remote · SA",           type: "Permanent", time: "1d ago",  locked: true  },
  { title: "Data Analyst (Power BI)",      loc: "Durban, KZN",           type: "Contract",  time: "1d ago",  locked: true  },
];

const TYPE_COLORS = {
  Contract:  { bg: "rgba(123,130,255,0.18)", color: "#9DA4FF" },
  Freelance: { bg: "rgba(0,200,150,0.14)",   color: "#00C896" },
  Permanent: { bg: "rgba(245,166,35,0.14)",  color: "#F5A623" },
};

function ProductMockup() {
  return (
    <div className={styles.browser}>
      <div className={styles.browserChrome}>
        <div className={styles.browserDots}>
          <span style={{ background: "#FF5F57" }} />
          <span style={{ background: "#FFBD2E" }} />
          <span style={{ background: "#28C840" }} />
        </div>
        <div className={styles.browserUrl}>gigscout.co.za/jobs</div>
      </div>
      <div className={styles.browserBody}>
        <div className={styles.miniSidebar}>
          <div className={styles.miniLogo}>
            <span className={styles.miniLogoMark}>GS</span>
            <span className={styles.miniLogoName}>Gigscout</span>
          </div>
          <div className={styles.miniNav}>
            {["Dashboard", "Browse Jobs", "My Profile", "Credits"].map((label, i) => (
              <div key={i} className={`${styles.miniNavItem} ${i === 1 ? styles.miniNavActive : ""}`}>
                {label}
              </div>
            ))}
          </div>
          <div className={styles.miniCredit}>
            <div className={styles.miniCreditTop}>
              <span className={styles.miniCreditLabel}>Credits</span>
              <span className={styles.miniCreditNum}>5</span>
            </div>
            <div className={styles.miniCreditBar}>
              <div className={styles.miniCreditFill} />
            </div>
          </div>
        </div>

        <div className={styles.miniContent}>
          <div className={styles.miniSearchBar}>
            <span className={styles.miniSearchIcon}>⌕</span>
            <span className={styles.miniSearchText}>e.g. React Developer, Graphic Designer…</span>
          </div>
          <div className={styles.miniCards}>
            {MOCK_JOBS.map((job, i) => {
              const tc = TYPE_COLORS[job.type];
              return (
                <div key={i} className={styles.miniCard}>
                  <div className={styles.miniCardHead}>
                    <span className={styles.miniCardTitle}>{job.title}</span>
                    <span className={styles.miniCardBadge} style={{ background: tc.bg, color: tc.color }}>
                      {job.type}
                    </span>
                  </div>
                  <div className={styles.miniCardMeta}>GigScout · {job.loc}</div>
                  <div className={styles.miniCardFoot}>
                    <span className={styles.miniCardTime}>{job.time}</span>
                    {job.locked
                      ? <span className={styles.miniCardLock}>🔒 1 credit</span>
                      : <span className={styles.miniCardOpen}>✓ Unlocked</span>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className={styles.browserFade} />
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className={styles.page}>

      {/* ── Nav ── */}
      <nav className={styles.nav}>
        <div className={styles.navInner}>
          <div className={styles.navLogo}>
            <span className={styles.navLogoMark}>GS</span>
            <span className={styles.navLogoName}>Gigscout</span>
          </div>
          <div className={styles.navLinks}>
            <a href="#how-it-works" className={styles.navLink}>How it works</a>
            <a href="#pricing"      className={styles.navLink}>Pricing</a>
          </div>
          <div className={styles.navActions}>
            <button className={styles.navSignIn} onClick={() => navigate("/login")}>Sign in</button>
            <button className={styles.navCta}    onClick={() => navigate("/register")}>Get started →</button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroLeft}>
            <div className={styles.heroBadge}>
              <span className={styles.heroBadgeDot} />
              Now live across South Africa
            </div>
            <h1 className={styles.heroTitle}>
              One feed.<br />
              Every opportunity<br />
              in Mzansi.
            </h1>
            <p className={styles.heroSub}>
              GigScout aggregates thousands of freelance and permanent roles
              from across South Africa into a single, clean job feed —
              updated daily. Browse free, unlock what interests you.
            </p>
            <div className={styles.heroCtas}>
              <button className={styles.ctaPrimary} onClick={() => navigate("/register")}>
                Get started free
              </button>
              <button className={styles.ctaGhost} onClick={() => navigate("/login")}>
                Sign in <ArrowRight size={14} strokeWidth={2} />
              </button>
            </div>
            <div className={styles.heroTrust}>
              <span>✓ Free to browse</span>
              <span>✓ 5 credits on signup</span>
              <span>✓ No CV required</span>
            </div>
          </div>
          <div className={styles.heroRight}>
            <ProductMockup />
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <div className={styles.statsBar}>
        <div className={styles.statsInner}>
          {[
            { num: "10,000+", label: "Active listings"  },
            { num: "Daily",   label: "Feed refreshed"   },
            { num: "SA-first",label: "Built for Mzansi" },
            { num: "Free",    label: "To start browsing" },
          ].map((s, i) => (
            <div key={i} className={styles.statGroup}>
              {i > 0 && <div className={styles.statDivider} />}
              <div className={styles.stat}>
                <div className={styles.statNum}>{s.num}</div>
                <div className={styles.statLabel}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── How it works ── */}
      <section className={styles.section} id="how-it-works">
        <div className={styles.sectionInner}>
          <div className={styles.sectionTag}>How it works</div>
          <h2 className={styles.sectionTitle}>Up and running in minutes</h2>
          <p className={styles.sectionSub}>
            No lengthy onboarding. No CV upload required to browse. Sign up and you're searching in under a minute.
          </p>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNum}>01</div>
              <h3 className={styles.stepTitle}>Create your profile</h3>
              <p className={styles.stepDesc}>
                Sign up with Google in one click. Add your skills and we surface relevant roles automatically — no manual setup.
              </p>
            </div>
            <div className={styles.stepConnector} />
            <div className={styles.step}>
              <div className={styles.stepNum}>02</div>
              <h3 className={styles.stepTitle}>Browse the full feed</h3>
              <p className={styles.stepDesc}>
                Search by role, location, or category. Switch between freelance gigs and permanent positions. Every listing you can see upfront.
              </p>
            </div>
            <div className={styles.stepConnector} />
            <div className={styles.step}>
              <div className={styles.stepNum}>03</div>
              <h3 className={styles.stepTitle}>Unlock and apply</h3>
              <p className={styles.stepDesc}>
                Spend 1 credit to reveal the apply link. Go straight to the original listing. No middleman, no recruiter call, no ghosting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className={styles.featuresSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionTag}>Why GigScout</div>
          <h2 className={styles.sectionTitle}>Built differently from job boards you know</h2>
          <div className={styles.featureGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}><Briefcase size={20} strokeWidth={2} /></div>
              <h3 className={styles.featureTitle}>Real SA listings, no noise</h3>
              <p className={styles.featureDesc}>
                We pull roles from across the South African market every day and deduplicate them into one clean feed. No ghost listings, no recycled posts.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}><Zap size={20} strokeWidth={2} /></div>
              <h3 className={styles.featureTitle}>Apply directly, no middleman</h3>
              <p className={styles.featureDesc}>
                Unlock a listing and land on the original source. No cover letters required to browse, no recruiter taking a percentage of your rate.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}><Search size={20} strokeWidth={2} /></div>
              <h3 className={styles.featureTitle}>Freelance and permanent, together</h3>
              <p className={styles.featureDesc}>
                Whether you're after a short contract or a full-time role, GigScout covers both. Switch between them with one click — no separate search.
              </p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon}><Shield size={20} strokeWidth={2} /></div>
              <h3 className={styles.featureTitle}>Your data stays yours</h3>
              <p className={styles.featureDesc}>
                We don't sell your profile to recruiters or share your details without permission. You decide what to unlock and when to apply.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className={styles.section} id="pricing">
        <div className={styles.sectionInner}>
          <div className={styles.sectionTag}>Pricing</div>
          <h2 className={styles.sectionTitle}>Simple, transparent credits</h2>
          <p className={styles.sectionSub}>
            Browse every listing for free. Buy credits when you're ready to apply — no subscriptions, no monthly fees.
          </p>
          <div className={styles.pricingGrid}>
            <div className={styles.pricingCard}>
              <div className={styles.pricingName}>Free</div>
              <div className={styles.pricingPrice}>R0</div>
              <div className={styles.pricingCredits}>5 credits on signup</div>
              <ul className={styles.pricingList}>
                <li><span className={styles.check}>✓</span> Browse all listings</li>
                <li><span className={styles.check}>✓</span> 5 free unlocks to start</li>
                <li><span className={styles.check}>✓</span> Google sign-in</li>
                <li><span className={styles.check}>✓</span> No card required</li>
              </ul>
              <button className={styles.pricingBtn} onClick={() => navigate("/register")}>
                Get started free
              </button>
            </div>

            <div className={`${styles.pricingCard} ${styles.pricingPop}`}>
              <div className={styles.pricingPopBadge}>Most popular</div>
              <div className={styles.pricingName}>Pro</div>
              <div className={styles.pricingPrice}>R99</div>
              <div className={styles.pricingCredits}>30 credits · R3.30 per unlock</div>
              <ul className={styles.pricingList}>
                <li><span className={styles.check}>✓</span> 30 job unlocks</li>
                <li><span className={styles.check}>✓</span> Best value per credit</li>
                <li><span className={styles.check}>✓</span> Instant delivery</li>
                <li><span className={styles.check}>✓</span> Paid via Yoco</li>
              </ul>
              <button className={styles.pricingBtnPrimary} onClick={() => navigate("/register")}>
                Get started
              </button>
            </div>

            <div className={styles.pricingCard}>
              <div className={styles.pricingName}>Starter</div>
              <div className={styles.pricingPrice}>R49</div>
              <div className={styles.pricingCredits}>10 credits · R4.90 per unlock</div>
              <ul className={styles.pricingList}>
                <li><span className={styles.check}>✓</span> 10 job unlocks</li>
                <li><span className={styles.check}>✓</span> No commitment</li>
                <li><span className={styles.check}>✓</span> Instant delivery</li>
                <li><span className={styles.check}>✓</span> Paid via Yoco</li>
              </ul>
              <button className={styles.pricingBtn} onClick={() => navigate("/register")}>
                Get started
              </button>
            </div>
          </div>
          <p className={styles.pricingNote}>
            Payments processed by Yoco · SSL encrypted · No card details stored by GigScout
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaSectionInner}>
          <h2 className={styles.ctaSectionTitle}>Start finding work today.</h2>
          <p className={styles.ctaSectionSub}>
            Join South African professionals finding freelance and permanent work on GigScout. Free to start, no commitment.
          </p>
          <button className={styles.ctaSectionBtn} onClick={() => navigate("/register")}>
            Create free account →
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerLogo}>
            <span className={styles.navLogoMark}>GS</span>
            <span className={styles.navLogoName}>Gigscout</span>
          </div>
          <div className={styles.footerLinks}>
            <a href="#how-it-works">How it works</a>
            <a href="#pricing">Pricing</a>
            <button className={styles.footerBtn} onClick={() => navigate("/login")}>Sign in</button>
            <button className={styles.footerBtn} onClick={() => navigate("/register")}>Get started</button>
          </div>
          <p className={styles.footerCopy}>© 2025 GigScout · Made in South Africa</p>
        </div>
      </footer>

    </div>
  );
}
